
import { useState } from 'react';

export default function CTASection() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // フォームデータにページ情報を追加
    const submitData = {
      formName: 'school-consultation-form',
      sourceUrl: window.location.href,
      submittedAt: new Date().toISOString(),
    };
    
    // フォームデータを追加
    formData.forEach((value, key) => {
      submitData[key] = value.toString();
    });

    try {
      const response = await fetch('https://n8n.srv1003858.hstgr.cloud/webhook/d6682ee0-8cb2-4bec-905a-ddf9d59b9685', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gray-50" data-cta-section>
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-[#2A6041] mb-4">
            さあ、あなたの農業の未来を相談しませんか？
          </h2>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            「うちの畑でも使える？」「補助金は？」「費用対効果は？」<br />
            どんな些細なことでも構いません。専門スタッフが、あなたの状況を丁寧にヒアリングし、<br />
            最適なプランをご提案します。相談は無料です。
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* お見積り・無料相談フォーム */}
          <div className="h-full">
            <form 
              id="school-consultation-form" 
              data-readdy-form="true" 
              className="bg-white rounded-2xl p-8 shadow-lg h-full flex flex-col"
              onSubmit={handleSubmit}
            >
              <h3 className="text-2xl font-bold text-[#2A6041] mb-6">お見積り・無料相談フォーム</h3>
              
              <div className="flex-1">
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      お名前 <span className="text-red-500">*</span>
                    </label>
                    <input 
                      required 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm" 
                      placeholder="山田太郎" 
                      name="name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">会社名・農場名</label>
                    <input 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm" 
                      placeholder="○○農場" 
                      name="company"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      電話番号 <span className="text-red-500">*</span>
                    </label>
                    <input 
                      required 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm" 
                      placeholder="090-1234-5678" 
                      type="tel" 
                      name="phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      メールアドレス <span className="text-red-500">*</span>
                    </label>
                    <input 
                      required 
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm" 
                      placeholder="example@email.com" 
                      type="email" 
                      name="email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      都道府県 <span className="text-red-500">*</span>
                    </label>
                    <select name="prefecture" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8">
                      <option value="">選択してください</option>
                      <option value="佐賀県">佐賀県</option>
                      <option value="鹿児島県">鹿児島県</option>
                      <option value="福岡県">福岡県</option>
                      <option value="長崎県">長崎県</option>
                      <option value="熊本県">熊本県</option>
                      <option value="大分県">大分県</option>
                      <option value="宮崎県">宮崎県</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">作物の種類</label>
                    <select name="cropType" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8">
                      <option value="">選択してください</option>
                      <option value="水稲">水稲</option>
                      <option value="麦">麦</option>
                      <option value="大豆">大豆</option>
                      <option value="野菜">野菜</option>
                      <option value="果樹">果樹</option>
                      <option value="その他">その他</option>
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    農地面積（概算）
                  </label>
                  <select name="farmSize" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8">
                    <option value="">選択してください</option>
                    <option value="1ha未満">1ha未満</option>
                    <option value="1-5ha">1-5ha</option>
                    <option value="5-10ha">5-10ha</option>
                    <option value="10-20ha">10-20ha</option>
                    <option value="20ha以上">20ha以上</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    興味のある製品・サービス
                  </label>
                  <select name="interestedProducts" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8">
                    <option value="">選択してください</option>
                    <option value="農業ドローン">農業ドローン</option>
                    <option value="自動操舵システム">自動操舵システム</option>
                    <option value="水管理システム">水管理システム</option>
                    <option value="ドローンスクール">ドローンスクール</option>
                    <option value="まだ決まっていない">まだ決まっていない</option>
                  </select>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    問い合わせ先 <span className="text-red-500">*</span>
                  </label>
                  <select 
                    name="contactDestination" 
                    required 
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#76B947] text-sm pr-8"
                  >
                    <option value="">選択してください</option>
                    <option value="IDS佐賀教習所（株式会社アイテム）">IDS佐賀教習所（株式会社アイテム）</option>
                    <option value="IDS川副教習所（九三エンジニアリング株式会社）">IDS川副教習所（九三エンジニアリング株式会社）</option>
                    <option value="IDS鹿児島教習所（株式会社エコナーセリー）">IDS鹿児島教習所（株式会社エコナーセリー）</option>
                    <option value="問合せ先がわからない">問合せ先がわからない</option>
                  </select>
                </div>

                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#76B947] focus:border-transparent resize-none text-sm"
                    placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                  ></textarea>
                </div>
              </div>

              <div className="mt-auto">
                <button 
                  className="w-full py-4 bg-[#FFC107] text-black rounded-lg hover:bg-yellow-500 transition-colors font-bold text-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed" 
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '送信中...' : '無料相談を申し込む'}
                </button>

                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm">お問い合わせありがとうございます。担当者より折り返しご連絡いたします。</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">送信に失敗しました。お手数ですが、もう一度お試しください。</p>
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* 電話相談セクション */}
          <div className="h-full">
            <div className="bg-white rounded-2xl p-8 shadow-lg h-full flex flex-col">
              <h3 className="text-2xl font-bold text-[#2A6041] mb-6 text-center">お電話で今すぐ相談したい方</h3>
              
              <div className="flex-1 flex flex-col justify-center">
                <div className="space-y-8">
                  <div className="text-center border-b border-gray-100 pb-6">
                    <h4 className="font-bold text-[#76B947] mb-3 text-lg">IDS佐賀教習所</h4>
                    <p className="text-sm text-gray-600 mb-2">株式会社アイテム</p>
                    <div className="flex items-center justify-center mb-2">
                      <i className="ri-map-pin-line text-[#76B947] mr-2 text-sm"></i>
                      <p className="text-sm text-gray-600">佐賀県多久市北多久町大字小侍4771</p>
                    </div>
                    <a href="tel:0952-71-9855" className="inline-flex items-center text-2xl font-bold text-[#2A6041] hover:text-[#76B947] transition-colors cursor-pointer mb-1">
                      <i className="ri-phone-line mr-2"></i>
                      0952-71-9855
                    </a>
                    <p className="text-sm text-gray-500">受付時間: 平日9:00〜17:00</p>
                  </div>
                  
                  <div className="text-center border-b border-gray-100 pb-6">
                    <h4 className="font-bold text-[#76B947] mb-3 text-lg">IDS川副教習所</h4>
                    <p className="text-sm text-gray-600 mb-2">九三エンジニアリング株式会社</p>
                    <div className="flex items-center justify-center mb-2">
                      <i className="ri-map-pin-line text-[#76B947] mr-2 text-sm"></i>
                      <p className="text-sm text-gray-600">佐賀県佐賀市川副町小々森146-1</p>
                    </div>
                    <a href="tel:0952-97-5291" className="inline-flex items-center text-2xl font-bold text-[#2A6041] hover:text-[#76B947] transition-colors cursor-pointer mb-1">
                      <i className="ri-phone-line mr-2"></i>
                      0952-97-5291
                    </a>
                    <p className="text-sm text-gray-500">受付時間: 平日9:00〜17:00</p>
                  </div>
                  
                  <div className="text-center pb-4">
                    <h4 className="font-bold text-[#76B947] mb-3 text-lg">IDS鹿児島教習所</h4>
                    <p className="text-sm text-gray-600 mb-2">株式会社エコナーセリー</p>
                    <div className="flex items-center justify-center mb-2">
                      <i className="ri-map-pin-line text-[#76B947] mr-2 text-sm"></i>
                      <p className="text-sm text-gray-600">鹿児島県日置市伊集院町古城795</p>
                    </div>
                    <a href="tel:099-213-9466" className="inline-flex items-center text-2xl font-bold text-[#2A6041] hover:text-[#76B947] transition-colors cursor-pointer mb-1">
                      <i className="ri-phone-line mr-2"></i>
                      099-213-9466
                    </a>
                    <p className="text-sm text-gray-500">受付時間: 平日9:00〜17:00</p>
                  </div>
                </div>
              </div>
              
              <div className="mt-auto pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-500 text-center leading-relaxed">
                  無理な営業は一切いたしませんので、<br />
                  お気軽にご連絡ください。
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
